import { useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import useAuth from '@/hooks/useAuth';
import { UserService } from '@/ui/_apiUrls';
import { useUser } from '@/contexts/UserContext/UserContext';
import { toast } from 'react-toastify';

function PreviewDeploy() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { useAuthenticatedPage } = useAuth();
  const { user, setUser } = useUser();
  useAuthenticatedPage();
  const template = searchParams.get('template') || '1';

  // Get the patch mutation from UserService
  const { mutate: patchUser, isLoading: isPatchingUser } = UserService.patch({
    mutationParams: {
      onSuccess: (response) => {
        // Update user context with the response
        const updatedUser = {
          ...user,
          ...response.result,
        };
        setUser(updatedUser);
        
        // Set the flag that the user has completed the full setup
        localStorage.setItem('hasCompletedProfileSetup', 'true');
        
        // Navigate to links page
        navigate('/links');
      },
      onError: () => {

        toast.error('Failed to save template selection');
      }
    }
  });

  const handlePrevious = () => {
    navigate('/add-links');
  };

  const handleDeploy = async () => {
    // Save the template to the user's profile when deploying
    if (user) {
      patchUser({ template: parseInt(template, 10) });
    } else {
      // If user is not available, still navigate to links page
      localStorage.setItem('hasCompletedProfileSetup', 'true');
      navigate('/links');
    }
  };

  // Generate preview URL
  const previewUrl = user?.title 
    ? `${import.meta.env.VITE_SITE_URL}/${user.title}`
    : `${import.meta.env.VITE_SITE_URL}/preview`;

  return (
    <div className="flex h-full w-full flex-col bg-[#F8F8FF] md:bg-white">
      {/* Header */}
      <div className="flex items-center justify-center px-4 py-6 md:px-8 md:py-8 md:bg-[#F8F8FF] md:rounded-t-[2rem]">
        <div className="text-center">
          <p className="text-sm font-medium text-gray-600 mb-1 md:text-base">Step 3</p>
          <h1 className="text-2xl font-bold text-gray-900 md:text-4xl">Preview and Deploy</h1>
        </div>
      </div>

      {/* Main Content - Live Preview Only */}
      <div className="flex-1 overflow-hidden md:bg-white md:mx-8 md:rounded-b-[2rem]">
        <div className="h-full w-full md:max-w-5xl md:mx-auto">
          <div className="h-full p-4 md:py-8 md:px-6">
            {/* Live Preview Header */}
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="text-center md:text-left">
                <h3 className="text-lg font-semibold text-gray-700 md:text-xl">Your Live Page Preview</h3>
                <p className="text-sm text-gray-500">This is how your page will look to visitors</p>
              </div>
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 rounded-lg bg-[#039BE5] px-4 py-3 text-white transition-colors hover:bg-[#0288D1] md:px-6 md:py-3"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Open in New Tab
              </a>
            </div>

            {/* Live Preview Frame */}
            <div className="h-[calc(100%-120px)] md:h-[calc(100%-80px)] rounded-lg border border-gray-200 bg-white shadow-sm">
              <iframe
                src={previewUrl}
                className="h-full w-full rounded-lg"
                title="Live Preview"
                sandbox="allow-scripts allow-same-origin"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Footer - Clean, no borders */}
      <div className="flex items-center justify-center gap-4 px-4 py-4 md:px-8 md:py-6 md:bg-[#F8F8FF] md:rounded-b-[2rem]">
        <button
          onClick={handlePrevious}
          className="flex h-12 w-[166.5px] max-w-[470px] items-center justify-center rounded-lg border border-[#039BE5] bg-transparent px-5 py-3 text-[#039BE5] transition-colors hover:bg-[#039BE5]/10 md:h-14 md:w-[200px] md:text-lg"
          type="button"
          disabled={isPatchingUser}
        >
          Previous
        </button>
        <button
          onClick={handleDeploy}
          className="flex h-12 w-[166.5px] max-w-[470px] items-center justify-center rounded-lg bg-[#039BE5] px-5 py-3 text-white transition-colors hover:bg-[#0288D1] md:h-14 md:w-[200px] md:text-lg"
          type="button"
          disabled={isPatchingUser}
        >
          {isPatchingUser ? 'Deploying...' : 'Deploy'}
        </button>
      </div>
    </div>
  );
}

export default PreviewDeploy;